import React, { useState, useImperativeHandle, forwardRef } from 'react';

const PaymentWidget = forwardRef((props, ref) => {
  const [showWidget, setShowWidget] = useState(false);
  const [amount, setAmount] = useState(5);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const predefinedAmounts = [3, 5, 10, 25, 50];

  // Expose showWidget function to parent components
  useImperativeHandle(ref, () => ({
    showModal: () => setShowWidget(true),
    hideModal: () => setShowWidget(false)
  }));

  const handlePayment = async () => {
    setLoading(true);
    setMessage('');

    try {
      const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : '/api';
      const apiUrl = `${apiBase}/create-payment-intent`;

      // Call our API to create payment session
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount * 100, // Convert to cents
          currency: 'usd'
        })
      });

      if (!response.ok) {
        throw new Error('Payment setup failed');
      }

      const { sessionId, url } = await response.json();

      // Redirect directly to Stripe Checkout
      if (url) {
        window.location.href = url;
      } else {
        // Fallback: use Stripe.js
        const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here');
        
        if (stripe) {
          const { error } = await stripe.redirectToCheckout({
            sessionId: sessionId
          });

          if (error) {
            setMessage(`Payment failed: ${error.message}`);
          }
        } else {
          setMessage('Payment system not available');
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      setMessage('Payment setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button 
        className="support-button"
        onClick={() => setShowWidget(true)}
        title="Tip Starfish Group"
      >
        ☕ Tip Us
      </button>

      {showWidget && (
        <div className="payment-modal-overlay" onClick={() => setShowWidget(false)}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="payment-close-btn"
              onClick={() => setShowWidget(false)}
              title="Close"
            >
              ✕
            </button>
            
            <div className="payment-content">
              <h3>Support ForceSight</h3>
              <p>Help us keep ForceSight free and improve it further!</p>
              
              <div className="amount-selection">
                <label>Choose amount:</label>
                <div className="amount-buttons">
                  {predefinedAmounts.map(value => (
                    <button
                      key={value}
                      className={`amount-btn ${amount === value ? 'selected' : ''}`}
                      onClick={() => setAmount(value)}
                    >
                      ${value}
                    </button>
                  ))}
                </div>
                <div className="custom-amount">
                  <label>Or custom amount:</label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={amount}
                    onChange={(e) => setAmount(parseInt(e.target.value) || 1)}
                    className="amount-input"
                  />
                </div>
              </div>

              {message && (
                <div className="payment-message">
                  {message}
                </div>
              )}

              <button
                className="pay-button"
                onClick={handlePayment}
                disabled={loading}
                title="You'll be redirected to Stripe's secure checkout"
              >
                {loading ? 'Processing...' : `Pay $${amount} with Stripe`}
              </button>

              <div className="payment-security">
                <p>🔒 Secure payment powered by Stripe</p>
                <p>Your payment information is encrypted and secure</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .support-button {
          position: fixed;
          bottom: 20px;
          right: 20px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 25px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
          transition: all 0.3s ease;
          z-index: 9998;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .support-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.5);
        }

        .payment-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(5px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 2rem;
        }

        .payment-modal {
          position: relative;
          background: var(--space-gradient, linear-gradient(135deg, #1a1b3a, #2d1b69));
          border: 2px solid var(--force-cyan, #00d4ff);
          border-radius: 16px;
          padding: 2rem;
          max-width: 400px;
          width: 100%;
          box-shadow: 0 20px 40px rgba(0, 212, 255, 0.3);
        }

        .payment-close-btn {
          position: absolute;
          top: -10px;
          right: -10px;
          background: var(--sith-red, #ff4444);
          color: white;
          border: none;
          width: 30px;
          height: 30px;
          border-radius: 15px;
          cursor: pointer;
          font-size: 1.2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }

        .payment-close-btn:hover {
          background: #ff0000;
        }

        .payment-content {
          color: white;
          text-align: center;
        }

        .payment-content h3 {
          margin: 0 0 1rem;
          color: var(--force-cyan, #00d4ff);
          font-size: 1.5rem;
        }

        .payment-content p {
          margin: 0 0 1.5rem;
          color: #b8c5d6;
        }

        .amount-selection {
          margin: 1.5rem 0;
        }

        .amount-selection label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: bold;
          color: #e2e8f0;
        }

        .amount-buttons {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }

        .amount-btn {
          background: rgba(99, 102, 241, 0.2);
          border: 2px solid #6366f1;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          cursor: pointer;
          font-weight: bold;
          transition: all 0.2s;
        }

        .amount-btn:hover {
          background: rgba(99, 102, 241, 0.3);
        }

        .amount-btn.selected {
          background: #6366f1;
          color: white;
        }

        .custom-amount {
          margin-top: 1rem;
        }

        .amount-input {
          width: 80px;
          padding: 0.5rem;
          border: 2px solid #6366f1;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          text-align: center;
          font-weight: bold;
          margin-left: 0.5rem;
        }

        .pay-button {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-weight: bold;
          font-size: 1rem;
          cursor: pointer;
          width: 100%;
          margin: 1rem 0;
          transition: all 0.2s;
        }

        .pay-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
        }

        .pay-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .payment-message {
          padding: 0.75rem;
          border-radius: 6px;
          margin: 1rem 0;
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid #ef4444;
          color: #fecaca;
        }

        .payment-security {
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.2);
        }

        .payment-security p {
          margin: 0.25rem 0;
          font-size: 0.8rem;
          color: #9ca3af;
        }

        @media (max-width: 768px) {
          .payment-modal {
            max-width: 90vw;
            padding: 1.5rem;
          }
          
          .amount-buttons {
            gap: 0.25rem;
          }
          
          .amount-btn {
            padding: 0.4rem 0.8rem;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </>
  );
});

export default PaymentWidget;